import useAbility from '@/hooks/use-ability'
import React, { useEffect, useState, useRef } from 'react'
import { FaShare } from 'react-icons/fa'
import {
  IoIosArrowDropleftCircle,
  IoIosArrowDroprightCircle,
} from 'react-icons/io'
import { IoMdClose } from 'react-icons/io'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'

const InitialParcelCardView = ({
  parcelData,
  handleInitialParcelSelection,
  queryApn,
  removeInitialParcels,
  initialApnSource,
  setCurrentIndex,
  currentIndex,
  setClickedGeojson,
  setShowInitialRightClickPopup,
  setInitialMarker,
  initialParcelLayersData,
}) => {
  const [isScrollable, setIsScrollable] = useState(false)
  const scrollRef = useRef(null)
  const cardRefs = useRef([]) // Store refs for each card
  const { checkAbility } = useAbility()
  const enableApnDisplay = checkAbility('apn_display')
  const enableOwnershipInformation = checkAbility('ownership_information')

  const handlePrev = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= 350
    }
  }

  const handleNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += 350
    }
  }

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollRef.current) {
        setIsScrollable(
          scrollRef.current.scrollWidth > scrollRef.current.clientWidth + 1
        )
      }
    }

    checkScrollable()
    window.addEventListener('resize', checkScrollable)

    return () => {
      window.removeEventListener('resize', checkScrollable)
    }
  }, [parcelData])

  const scrollToCurrentCard = () => {
    if (cardRefs.current[currentIndex]) {
      cardRefs.current[currentIndex].scrollIntoView({
        behavior: 'smooth', // Smooth scrolling
        block: 'nearest', // Ensures it doesn't scroll too far
        inline: 'center', // Align the selected card in the center
      })
    }
  }

  useEffect(() => {
    if (currentIndex >= parcelData.length || currentIndex === -1) return
    handleInitialParcelSelection(parcelData[currentIndex])
    scrollToCurrentCard()
  }, [currentIndex])

  useEffect(() => {
    if (parcelData.length > 0) {
      setCurrentIndex(parcelData.length - 1)
    } else {
      setCurrentIndex(-1)
    }
  }, [parcelData])

  const styles = {
    container: {
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100vw',
      zIndex: 200,
      minHeight: '140px',
    },
    scrollWrapper: {
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'hidden',
      scrollBehavior: 'smooth',
      scrollbarWidth: 'none',
      minHeight: '140px',
      alignItems: 'center',
      padding: '4px',
    },
    cardView: {
      position: 'relative', // Ensure the close button stays relative to the card
      display: 'flex',
      minWidth: '370px', // Increased width
      maxWidth: '370px', // Increased width
      minHeight: '110px',
      maxHeight: '150px',
      margin: '0 8px',
      border: '1px solid #ccc',
      borderRadius: '8px',
      overflow: 'visible', // Ensure shadow is not clipped
      fontFamily: 'Roboto, "Noto Sans KR", Arial, sans-serif',
      backgroundColor: 'white',
      transition: 'all 0.3s ease',
    },
    cardViewCurrent: {
      transform: 'scale(1.05)',
      boxShadow: '0px 0px 2px 2px #00ac9c',
      border: '0px',
      zIndex: 202,
    },
    arrowButton: {
      fontSize: '30px',
      cursor: 'pointer',
      color: '#00ac9c',
      zIndex: 204,
      background: 'none',
      border: 'none',
    },
    centerSection: {
      flex: 1,
      minWidth: '250px',
      maxHeight: '150px',
      padding: '12px', // Padding added here
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    scrollableContent: {
      flex: 1,
      maxHeight: '105px', // Deducting padding space from total height
      overflowY: 'auto', // Scroll only within the inner section
    },
    locationRow: {
      display: 'flex',
      alignItems: 'center', // Vertically align items
      justifyContent: 'space-between', // Ensure proper spacing
      gap: '8px', // Optional spacing between elements
    },
    locationName: {
      fontSize: '14px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    address: {
      fontSize: '12px',
      color: '#666',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    coordinatesContainer: {
      display: 'flex',
      justifyContent: 'space-between', // Space between lat/lon and share button
      alignItems: 'center', // Vertically align content
      width: '100%', // Ensure it spans the available width
      marginTop: '8px', // Optional spacing from above content
    },
    coordinatesButton: {
      background: 'none',
      border: 'none',
      color: '#007bff',
      fontSize: '12px',
      cursor: 'pointer',
      textAlign: 'left',
      padding: 0,
      flex: 1, // Allow it to take up remaining space
    },
    shareButton: {
      color: '#00ac9c',
      fontSize: '12px',
      border: 'none',
      borderRadius: '4px',
      padding: '6px 12px',
      cursor: 'pointer',
      flexShrink: 0, // Prevent shrinking of the share button
      marginLeft: '8px', // Add spacing from the lat/lon button
    },
    rightSection: {
      width: '110px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: parcelData.length > 1 ? 'space-between' : 'center',
      alignItems: 'center',
      padding: parcelData.length > 1 ? '12px 12px 12px 0px' : '0 12px 0 0',
      gap: '8px',
    },
    closingSection: {
      width: '20px', // New closing section width
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '8px 4px',
    },
    actionButton: {
      background: '#00ac9c',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      color: '#ffffff',
      borderRadius: '4px',
      padding: '6px 12px',
      textAlign: 'center',
      width: '100%',
    },
    closeButton: {
      position: 'absolute',
      top: 0,
      right: -5,
      fontSize: '20px',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      color: '#333',
      zIndex: 205, // Ensures it stays on top of other elements
    },
    hideScrollbar: {
      '::WebkitScrollbar': {
        display: 'none',
      },
    },
  }

  return (
    <div style={styles.container}>
      {parcelData.length > 1 && isScrollable && (
        <button
          onClick={handlePrev}
          aria-label="Previous"
          style={styles.arrowButton}
        >
          <IoIosArrowDropleftCircle />
        </button>
      )}
      <div
        style={{ ...styles.scrollWrapper, ...styles.hideScrollbar }}
        ref={scrollRef}
      >
        {parcelData.map((parcel, index) => (
          <div
            key={parcel.data.apn}
            style={
              index === currentIndex
                ? { ...styles.cardView, ...styles.cardViewCurrent }
                : styles.cardView
            }
            ref={(el) => (cardRefs.current[index] = el)}
            onClick={() => {
              setInitialMarker(false)
              setShowInitialRightClickPopup(false)
              setClickedGeojson({
                type: 'FeatureCollection',
                features: [parcel.feature],
              })
            }}
          >
            <div style={styles.centerSection}>
              <div style={styles.scrollableContent}>
                <div style={styles.locationRow}>
                  <div className="relative">
                    <UpgradeLockIconLayout
                      className="absolute min-w-[60px]"
                      show={!enableApnDisplay}
                    />
                    <div
                      style={{
                        ...styles.locationName,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                      }}
                    >
                      <p>{parcel.data.apn}</p>
                    </div>
                  </div>
                  <p>-</p>
                  <p>{parcel.data.county}</p>
                  <button
                    className="initial-share"
                    aria-label="Share"
                    onClick={(e) => {
                      e.stopPropagation() // Prevent triggering the card's onClick
                      navigator
                        .share({
                          title: 'Hydra score parcel(s)',
                          text: 'Click link to see shared parcel(s)',
                          url: `https://app.hydrascore.com/?locations=${btoa(
                            JSON.stringify([
                              {
                                lat: parcel.data.geolocation.lat,
                                lon: parcel.data.geolocation.lng,
                              },
                            ])
                          )}`,
                        })
                        .catch((error) => {
                          console.error('Sharing failed', error)
                        })
                    }}
                    title="share this parcel"
                    style={styles.shareButton}
                  >
                    <FaShare />
                  </button>
                </div>
                <div className="relative" style={styles.address}>
                  <UpgradeLockIconLayout
                    className="absolute min-w-[60px] min-h-[20px]"
                    show={!enableOwnershipInformation}
                  />
                  {parcel.data.address}
                </div>
                <div
                  style={{
                    borderBottom: '1px solid #ccc',
                    margin: '8px 0', // Add some space around the divider
                  }}
                ></div>
                <button style={styles.coordinatesButton}>
                  {`${parcel.data.geolocation.lat}, ${parcel.data.geolocation.lng}`}
                </button>
                {index === currentIndex &&
                  Object.keys(initialParcelLayersData).length > 0 &&
                  Object.keys(initialParcelLayersData).map((layerName) => (
                    <div key={layerName} style={{ fontSize: '12px' }}>
                      <strong>{layerName}</strong>:{' '}
                      {initialParcelLayersData[layerName]}
                    </div>
                  ))}
              </div>
            </div>
            <div style={styles.rightSection}>
              <button
                aria-label="single"
                style={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation()
                  setInitialMarker(false)
                  setShowInitialRightClickPopup(false)
                  queryApn(
                    '',
                    '',
                    '',
                    '',
                    '',
                    JSON.stringify([parcel.feature]),
                    true
                  )
                }}
              >
                Query Data
              </button>
              {parcelData.length > 1 && (
                <button
                  aria-label="multiple"
                  style={styles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    setInitialMarker(false)
                    setShowInitialRightClickPopup(false)
                    queryApn(
                      '',
                      '',
                      '',
                      '',
                      '',
                      JSON.stringify(initialApnSource.data.features),
                      true
                    )
                  }}
                >
                  Query All
                </button>
              )}
            </div>
            <div style={styles.closingSection}>
              <button
                aria-label="Close"
                style={styles.closeButton}
                onClick={(e) => {
                  e.stopPropagation()
                  removeInitialParcels([parcel.data.apn])
                }}
              >
                <IoMdClose />
              </button>
            </div>
          </div>
        ))}
      </div>{' '}
      {parcelData.length > 1 && isScrollable && (
        <button
          onClick={handleNext}
          aria-label="Next"
          style={styles.arrowButton}
        >
          <IoIosArrowDroprightCircle />
        </button>
      )}
    </div>
  )
}
export default InitialParcelCardView
